import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  HelpCircle,
  LogOut,
  Mail,
  MousePointer,
  X,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { cn } from '../../utils/cn';
import DateRangePicker from '../ui/DateRangePicker';
import {
  getEmailSentHistoryRequest,
  clearEmailSentHistorySuccess,
  selectEmailSentHistoryData,
  selectEmailSentHistoryPagination,
  exportSentEmailsCSVRequest,
  clearExportSentState,
  selectExportSentBlob,
  selectExportSentLoading,
  selectExportSentError,
  selectExportSentSuccess,
} from '../../store/emailSentHistory';
import {
  IEmailSentHistory,
  EmailSentHistoryFilters,
} from '../../types/emailSentHistory/emailSentHistory.types';
import { Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';

interface SentMessagesProps {
  campaignId: string;
  campaignName?: string;
}

const SentMessages: React.FC<SentMessagesProps> = ({
  campaignId,
  campaignName,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { history, loading, error, success } = useSelector(
    selectEmailSentHistoryData
  );
  const pagination = useSelector(selectEmailSentHistoryPagination);
  const exportBlob = useSelector(selectExportSentBlob);
  const exportLoading = useSelector(selectExportSentLoading);
  const exportError = useSelector(selectExportSentError);
  const exportSuccess = useSelector(selectExportSentSuccess);

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [status, setStatus] = useState('All statuses');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [selectedEmailForResend, setSelectedEmailForResend] =
    useState<IEmailSentHistory | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;

      if (hasPaginationData) {
        if (currentPage !== pagination.page || pageSize !== pagination.limit) {
          setCurrentPage(pagination.page);
          setPageSize(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, currentPage, pageSize]);

  // Reset pagination when campaignId changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
    }
  }, [campaignId, isInitialized]);

  // Reset to first page when page size changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
    }
  }, [pageSize, isInitialized]);

  // Fetch email sent history when component mounts or campaignId changes
  useEffect(() => {
    if (isInitialized && campaignId) {
      const filters: EmailSentHistoryFilters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: status !== 'All statuses' ? status.toLowerCase() : undefined,
        dateRange:
          dateRange.startDate && dateRange.endDate
            ? {
                start: moment(dateRange.startDate).format('YYYY-MM-DD'),
                end: moment(dateRange.endDate).format('YYYY-MM-DD'),
              }
            : undefined,
        page: currentPage,
        limit: pageSize,
      };
      dispatch(getEmailSentHistoryRequest(campaignId, filters));
    }
  }, [
    dispatch,
    campaignId,
    status,
    debouncedSearchTerm,
    dateRange,
    currentPage,
    pageSize,
    isInitialized,
  ]);

  // Clear success message when component unmounts
  useEffect(() => {
    return () => {
      if (success) {
        dispatch(clearEmailSentHistorySuccess());
      }
      if (exportSuccess) {
        dispatch(clearExportSentState());
      }
    };
  }, [dispatch, success, exportSuccess]);

  // Handle export success
  useEffect(() => {
    if (exportBlob) {
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr =
        dateRange.startDate && dateRange.endDate
          ? `_${moment(dateRange.startDate).format('YYYY-MM-DD')}_${moment(dateRange.endDate).format('YYYY-MM-DD')}`
          : '';
      link.download = `sent_emails${dateRangeStr}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        title: 'Export Successful',
        message: 'Sent emails have been exported successfully.',
      });

      dispatch(clearExportSentState());
    }
  }, [exportBlob, dateRange, showToast, dispatch]);

  // Handle export error
  useEffect(() => {
    if (exportError) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: exportError,
      });
      dispatch(clearExportSentState());
    }
  }, [exportError, showToast, dispatch]);

  // Helper function to determine status from email history record
  const getEmailStatus = (emailHistory: IEmailSentHistory): string => {
    if (emailHistory.error || emailHistory.event?.failed) return 'failed';
    if (emailHistory.complaintAt) return 'complained';
    if (emailHistory.unsubscribedAt) return 'unsubscribed';
    if (emailHistory.spamAt) return 'spam';
    if (emailHistory.bouncedAt) return 'bounced';
    if (emailHistory.clickedEvents && emailHistory.clickedEvents.length > 0)
      return 'clicked';
    if (
      emailHistory.humanOpenedAt ||
      (emailHistory.openedEvents && emailHistory.openedEvents.length > 0)
    )
      return 'opened';
    if (emailHistory.deliveredAt) return 'delivered';
    if (emailHistory.sentAt) return 'sent';
    return 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'bg-success-background text-success border-success/30';
      case 'opened':
        return 'bg-primary-background text-primary border-primary/30';
      case 'clicked':
        return 'bg-info-background text-info border-info/30';
      case 'bounced':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'complained':
        return 'bg-warning-background text-warning border-warning/30';
      case 'unsubscribed':
        return 'bg-warning-background text-warning border-warning/30';
      case 'spam':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'failed':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle2 className="w-3.5 h-3.5" />;
      case 'opened':
        return <Eye className="w-3.5 h-3.5" />;
      case 'clicked':
        return <MousePointer className="w-3.5 h-3.5" />;
      case 'bounced':
      case 'complained':
      case 'spam':
        return <AlertCircle className="w-3.5 h-3.5" />;
      case 'unsubscribed':
        return <LogOut className="w-3.5 h-3.5" />;
      case 'failed':
        return <AlertCircle className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'opened':
        return 'Opened';
      case 'clicked':
        return 'Clicked';
      case 'bounced':
        return 'Bounced';
      case 'complained':
        return 'Complaint';
      case 'unsubscribed':
        return 'Unsubscribed';
      case 'spam':
        return 'Spam';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  // Helper function to format date using moment
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';

    const momentDate = moment(date);
    const now = moment();

    // Check if date is valid
    if (!momentDate.isValid()) return 'Invalid date';

    // Show relative time for recent dates
    if (momentDate.isAfter(now.subtract(1, 'hour'))) {
      return momentDate.fromNow();
    }

    // Show "Today" for today's dates
    if (momentDate.isSame(now, 'day')) {
      return `Today at ${momentDate.format('h:mm A')}`;
    }

    // Show "Yesterday" for yesterday's dates
    if (momentDate.isSame(now.subtract(1, 'day'), 'day')) {
      return `Yesterday at ${momentDate.format('h:mm A')}`;
    }

    // Show date and time for older dates
    if (momentDate.isSame(now, 'year')) {
      return momentDate.format('MMM D, h:mm A');
    }

    // Show full date for dates from different years
    return momentDate.format('MMM D, YYYY h:mm A');
  };

  // Helper function to get action text
  const getActionText = (emailHistory: IEmailSentHistory): string => {
    const status = getEmailStatus(emailHistory);
    switch (status) {
      case 'clicked':
        return `Email clicked (${emailHistory.clickedEvents?.length || 0} times)`;
      case 'opened':
        return `Email opened (${emailHistory.openedEvents?.length || 0} times)`;
      case 'bounced':
        return 'Email bounced';
      case 'complained':
        return 'Complaint received';
      case 'unsubscribed':
        return 'Contact unsubscribed';
      case 'spam':
        return 'Marked as spam';
      case 'failed':
        return 'Failed to send';
      case 'delivered':
        return 'Email delivered';
      default:
        return 'Email sent';
    }
  };

  // Helper function to clear all filters
  const clearAllFilters = () => {
    setDebouncedSearchTerm('');
    setDateRange({ startDate: null, endDate: null });
    setStatus('All statuses');
    setCurrentPage(0);
  };

  // Handle export
  const handleExport = () => {
    const startDate = dateRange.startDate
      ? moment(dateRange.startDate).format('YYYY-MM-DD')
      : undefined;
    const endDate = dateRange.endDate
      ? moment(dateRange.endDate).format('YYYY-MM-DD')
      : undefined;
    dispatch(exportSentEmailsCSVRequest(campaignId, startDate, endDate));
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Helper function to check if any filters are active
  const hasActiveFilters = () => {
    return (
      dateRange.startDate !== null ||
      dateRange.endDate !== null ||
      status !== 'All statuses'
    );
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Check if row is expanded
  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  // Helper function to get campaign name
  const getCampaignName = (): string => {
    return campaignName || 'Untitled Campaign';
  };

  // Modal handlers
  const handleResendClick = (emailHistory: IEmailSentHistory) => {
    setSelectedEmailForResend(emailHistory);
    setIsResendModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsResendModalOpen(false);
    setSelectedEmailForResend(null);
  };

  const handleModalSubmit = () => {
    // TODO: Implement resend logic here
    setIsResendModalOpen(false);
    setSelectedEmailForResend(null);
  };

  // Helper function to format delivery timeline
  const getDeliveryTimeline = (emailHistory: IEmailSentHistory): string[] => {
    const timeline: string[] = [];

    if (emailHistory.createdAt) {
      timeline.push(`Created ${formatDate(emailHistory.createdAt)}`);
    }

    if (emailHistory.sentAt) {
      timeline.push(`Sent ${formatDate(emailHistory.sentAt)}`);
    }

    if (emailHistory.deliveredAt) {
      timeline.push(`Delivered ${formatDate(emailHistory.deliveredAt)}`);
    }

    if (emailHistory.openedEvents && emailHistory.openedEvents.length > 0) {
      timeline.push(
        `Opened ${emailHistory.openedEvents.length} time${emailHistory.openedEvents.length > 1 ? 's' : ''}`
      );
    }

    if (emailHistory.clickedEvents && emailHistory.clickedEvents.length > 0) {
      timeline.push(
        `Clicked ${emailHistory.clickedEvents.length} time${emailHistory.clickedEvents.length > 1 ? 's' : ''}`
      );
    }

    return timeline;
  };

  return (
    <div className="flex-1">
      {/* Filters Section */}
      <div className="px-6 pt-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Filters */}
          <div className="flex items-center gap-4 flex-1">
            {/* Status Filter */}
            <div className="relative">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer min-w-[140px]"
              >
                <option>All statuses</option>
                <option>Sent</option>
                <option>Delivered</option>
                <option>Opened</option>
                <option>Clicked</option>
                <option>Bounced</option>
                <option value="complained">Complained</option>
                <option>Unsubscribed</option>
                <option>Spam</option>
                <option>Failed</option>
                <option>Pending</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            {/* Date Range Picker */}
            <div className="w-64">
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                placeholder="Select date range"
              />
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-muted/50 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              <span>{exportLoading ? 'Exporting...' : 'Export'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4 overflow-x-auto">
        {/* Messages Table */}
        <div className="rounded-xl border border-border mx-6 my-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Date Sent
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Recipient / Person
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <div className="flex items-center gap-1">
                    <span>Status</span>
                    <HelpCircle className="w-3 h-3 text-muted-foreground" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-8 px-4 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <Clock className="w-5 h-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground font-medium">
                        Loading messages...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="py-8 px-4 text-center">
                    <div className="flex items-center justify-center gap-3 text-destructive">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Error loading messages: {error}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : history.length > 0 ? (
                history.map(
                  (emailHistory: IEmailSentHistory, index: number) => {
                    const emailStatus = getEmailStatus(emailHistory);
                    const actionText = getActionText(emailHistory);
                    const displayDate = formatDate(
                      emailHistory.sentAt || emailHistory.createdAt
                    );
                    const rowId =
                      emailHistory._id ||
                      emailHistory.messageId ||
                      `row-${index}`;
                    const isExpanded = isRowExpanded(rowId);
                    const timeline = getDeliveryTimeline(emailHistory);
                    const displayCampaignName = getCampaignName();

                    return (
                      <React.Fragment key={rowId}>
                        {/* Main Row */}
                        <tr
                          className={cn(
                            'border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer'
                          )}
                          onClick={() => toggleRowExpansion(rowId)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(rowId);
                                }}
                                className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                )}
                              </button>
                              <span className="text-sm text-card-foreground">
                                {displayDate}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-sm text-card-foreground truncate">
                                {actionText}
                              </span>
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/contacts/${emailHistory.contactId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2"
                            >
                              <div className="text-sm text-primary hover:text-primary-hover cursor-pointer hover:underline truncate">
                                {emailHistory.email}
                              </div>
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                                getStatusColor(emailStatus)
                              )}
                            >
                              {getStatusIcon(emailStatus)}
                              <span>{getStatusText(emailStatus)}</span>
                            </span>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && (
                          <tr className="bg-muted/30">
                            <td colSpan={4} className="px-4 py-3">
                              <div className="pl-6 space-y-3">
                                {/* Campaign Information */}
                                <div className="flex items-center gap-4">
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[120px]">
                                    TRIGGERED CAMPAIGN:
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button className="text-sm text-primary hover:text-primary-hover font-medium transition-colors">
                                      {displayCampaignName}
                                    </button>
                                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    Created {formatDate(emailHistory.createdAt)}
                                  </span>
                                </div>

                                {/* Delivery Timeline */}
                                <div className="flex items-start gap-4">
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[120px]">
                                    TIMELINE:
                                  </span>
                                  <div className="space-y-1">
                                    {timeline.map((item, idx) => (
                                      <div
                                        key={idx}
                                        className="text-sm text-card-foreground"
                                      >
                                        {item}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 pt-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResendClick(emailHistory);
                                    }}
                                    className="px-3 py-1 text-xs font-medium text-foreground bg-muted/50 border border-border rounded-lg hover:bg-muted transition-colors"
                                  >
                                    Resend
                                  </button>
                                  <Link
                                    to={`/emails/view/${emailHistory._id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-3 py-1 text-xs font-medium text-foreground bg-muted/50 border border-border rounded-lg hover:bg-muted transition-colors"
                                  >
                                    View Details
                                  </Link>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }
                )
              ) : (
                <tr>
                  <td colSpan={4} className="py-12 px-4 text-center">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Mail className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold text-card-foreground mb-1">
                        No sent messages
                      </h3>
                      <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                        No messages have been sent yet for this campaign. Start
                        by creating and sending your first email.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && !error && history.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    {pagination.page * pagination.limit + 1}-
                    {Math.min(
                      (pagination.page + 1) * pagination.limit,
                      pagination.total
                    )}{' '}
                    of {pagination.total}
                  </div>

                  {/* Page Limit Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Show:</span>
                    <div className="relative">
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="appearance-none bg-card border border-border rounded-lg px-2 py-1 pr-8 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      per page
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={pagination.page === 0}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="text-sm text-muted-foreground px-3 py-1">
                    Page {pagination.page + 1} of {pagination.totalPages}
                  </span>

                  <button
                    className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page >= pagination.totalPages - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={pagination.page >= pagination.totalPages - 1}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resend Modal */}
      {isResendModalOpen && (
        <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Resend email
                </h3>
                {selectedEmailForResend && (
                  <p className="text-sm text-gray-600 mt-1">
                    To: {selectedEmailForResend?.email}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <div className="bg-warning-background border border-warning/30 rounded-md p-3 mb-4">
                  <p className="text-sm text-warning-foreground">
                    Heads up! You're about to send this message again. All
                    recipients (including BCCs) will be sent the exact same
                    content they were sent the last time.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How do you want to handle your message limit?
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="messageLimit"
                          value="override"
                          defaultChecked
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        <span className="text-sm text-gray-700">
                          Override the limit and send the message
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="messageLimit"
                          value="respect"
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        <span className="text-sm text-gray-700">
                          Do not send the message if it reaches the limit
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Do you want to track opens and clicks?
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="tracking"
                          value="yes"
                          defaultChecked
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        <span className="text-sm text-gray-700">
                          Yes, track opens and link clicks [recommended]
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="tracking"
                          value="no"
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        <span className="text-sm text-gray-700">
                          No, do not track opens and link clicks
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleModalCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleModalSubmit}
                  className="px-4 py-2 text-sm font-medium text-success-foreground bg-success border border-transparent rounded-md hover:bg-success-hover transition-colors"
                >
                  Resend message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentMessages;
